'use client';

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import FAQSection from '@/components/FAQSection';
import FilterTable from '@/components/FilterTable';
import LoadingOverlay from '@/components/LoadingOverlay';
import { statics } from '@/data/store';
import { COLORS } from '@/constants/colors';
import { formatMakeName, deduplicateModels } from '@/utils/formatters';
import { pedalPowerKitFaqs } from '@/data/faqData';
import { trackThrottlePageView, trackThrottleVehicleSearch } from '@/utils/analytics';

const tableHeaders = [
  { id: 0, name: 'gfu_part_num', label: 'Part Number', maxWidth: 150 },
  { id: 1, name: 'title', label: 'Product Name', maxWidth: 250 },
  { id: 2, name: 'description', label: 'Description', maxWidth: 300 },
];

const PowerPedalKitPage = () => {
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState<Array<Record<string, unknown>>>([]);

  // Vehicle filter state
  const [vehicleFilters, setVehicleFilters] = useState({
    year: '',
    make_id: '',
    model_name: '',
    engine: ''
  });

  const [dropdownData, setDropdownData] = useState<{
    make?: Array<{ id: string; title: string }>;
    name?: Array<{ name: string; title: string }>;
    engine?: Array<{ displacement: string; engine: string; intake: string; description: string }>;
  }>({});

  const [isFiltering, setIsFiltering] = useState(false);

  // Generate years array (1959 to next model year) - memoized for performance
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear() + 1;
    return Array.from({ length: currentYear - 1959 + 1 }, (_, i) => currentYear - i);
  }, []);

  const fetchProducts = async (applyVehicleFilter = false) => {
    try {
      setLoading(true);

      let url = `${statics.BaseUrl}/product?product_type_id=13&pageNo=1&pageSize=200&is_active=1`;

      if (applyVehicleFilter && vehicleFilters.year && vehicleFilters.make_id && vehicleFilters.model_name && vehicleFilters.engine) {
        url = `${statics.BaseUrl}/product-search?year=${vehicleFilters.year}&make_id=${vehicleFilters.make_id}&model_name=${vehicleFilters.model_name}&engine=${vehicleFilters.engine}&product_type_id=13`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (data.ResponseCode === 'Success' && data.data?.rows) {
        setTableData(data.data.rows);
      } else if (Array.isArray(data?.data)) {
        setTableData(data.data);
      } else {
        console.error('Failed to fetch products:', data.message);
        setTableData([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(); // eslint-disable-line react-hooks/set-state-in-effect
    // Track throttle page view for analytics
    trackThrottlePageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Progressive dropdown fetching - matches URElement1.tsx pattern
  useEffect(() => {
    const runFetch = async () => {
      const params = new URLSearchParams();
      params.append('product_type_id', '13');

      Object.entries(vehicleFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const url = `${statics.BaseUrl}/product-search?${params.toString()}`;
      try {
        const res = await fetch(url);
        const response = await res.json();

        if (response?.data?.length > 0) {
          const filterKeysArr = ['make', 'name', 'engine'];
          let index = -1;

          Object.keys(vehicleFilters).forEach((key) => {
            if (vehicleFilters[key as keyof typeof vehicleFilters] !== '' && vehicleFilters[key as keyof typeof vehicleFilters] !== '-1') index++;
          });

          const getKey = filterKeysArr[index];
          const objectKey = getKey as keyof typeof dropdownData;
          const filteredData = response.data;

          const allSelected = vehicleFilters.year && vehicleFilters.make_id && vehicleFilters.model_name && vehicleFilters.engine;

          if (objectKey) {
            if (getKey === 'engine') {
              filteredData.sort((a: { displacement: string }, b: { displacement: string }) => {
                if (a.displacement < b.displacement) return -1;
                if (a.displacement > b.displacement) return 1;
                return 0;
              });
            } else if (getKey === 'make' || getKey === 'name') {
              filteredData.sort((a: { name: string }, b: { name: string }) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
              });
            }

            if (!allSelected) {
              setDropdownData((prev) => ({ ...prev, [objectKey]: filteredData }));
            }
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    runFetch();
  }, [vehicleFilters, vehicleFilters.year, vehicleFilters.make_id, vehicleFilters.model_name, vehicleFilters.engine]);

  // Handler functions - matches URElement1.tsx pattern
  const handleVehicleFilterChange = (key: keyof typeof vehicleFilters, value: string) => {
    const tempArr: (keyof typeof vehicleFilters)[] = ['year', 'make_id', 'model_name', 'engine'];
    const index = tempArr.indexOf(key);
    tempArr.splice(0, index + 1);

    const tempData = { ...dropdownData };
    const tempStr = { ...vehicleFilters };

    tempArr.forEach((k) => {
      if (k === 'make_id') delete tempData.make;
      else if (k === 'model_name') delete tempData.name;
      else if (k === 'engine') delete tempData.engine;
      tempStr[k] = '';
    });

    setDropdownData(tempData);
    setVehicleFilters({ ...tempStr, [key]: value });
  };

  const handleSearch = () => {
    setIsFiltering(true);
    fetchProducts(true);
    // Track vehicle search for throttle products
    const makeName = dropdownData.make?.find((m) => m.id === vehicleFilters.make_id)?.title || '';
    trackThrottleVehicleSearch({
      year: vehicleFilters.year,
      make: formatMakeName(makeName),
      model: vehicleFilters.model_name,
    });
  };

  const handleClearFilters = () => {
    setVehicleFilters({ year: '', make_id: '', model_name: '', engine: '' });
    setDropdownData({});
    setIsFiltering(false);
    fetchProducts(false);
  };

  // Check if all filters are selected
  const allFiltersSelected = vehicleFilters.year && vehicleFilters.make_id && vehicleFilters.model_name && vehicleFilters.engine;

  // Get current make name for display
  const getCurrentMakeName = () => {
    const make = dropdownData.make?.find((m) => m.id === vehicleFilters.make_id);
    return make ? formatMakeName(make.title) : '';
  };

  return (
    <>
      <Header />
      <div className="header-gap-creator"></div>
      <Breadcrumbs items={[{ label: 'Pedal Power Kit' }]} />
      <LoadingOverlay loading={loading} />

      <main className="container-0">
        <PageWrapper>
          <h1>Pedal Power Kit - Build The Ultimate Power Bundle</h1>

          <IntroText>
            The Green Filter Pedal Power Kit pairs a throttle controller with the right Green Filter High Performance
            Air Filter to unlock a whole new world of performance and efficiency. They&rsquo;re the go-to air filters for
            endurance racers at Le Mans and the World Rally Championship. They&rsquo;re the filters first responders rely
            on. And they&rsquo;re the filter your car needs.
          </IntroText>

          <ImageGrid>
            <ImageBox>
              <img
                className="resize"
                alt="Pedal Power Kit - Throttle Controller and Air Filters Bundle"
                src="/assets/products/power-pedal-kit-bundle.png"
              />
            </ImageBox>
          </ImageGrid>

          <SectionTitle>Key Features & Benefits</SectionTitle>
          <BenefitsSection>
            <BenefitsList>
              <BenefitItem>Custom programming for each vehicle</BenefitItem>
              <BenefitItem>Quick acceleration</BenefitItem>
              <BenefitItem>Suitable for offroad and 4x4</BenefitItem>
              <BenefitItem>Better traction on ice, sand, mud, and snow with better fuel mileage</BenefitItem>
              <BenefitItem>Will not void vehicle warranty</BenefitItem>
            </BenefitsList>
          </BenefitsSection>

          <SectionTitle>Four Modes To Unleash</SectionTitle>
          <GuideText>
            The throttle controller is Bluetooth compatible - download the free app on{' '}
            <a href="#" style={{ color: COLORS.brandGreen, fontWeight: 600 }}>
              App Store
            </a>{' '}
            or{' '}
            <a href="#" style={{ color: COLORS.brandGreen, fontWeight: 600 }}>
              Google Play
            </a>{' '}
            to change modes from your phone.
          </GuideText>

          <ImageGrid>
            <ImageBox>
              <img
                src="/assets/four_modes.png"
                alt="Four driving modes - ECO, CITY, SPORT, and SPORT+"
                className="resize"
              />
            </ImageBox>
          </ImageGrid>

          <ModesGrid>
            <ModeCard>
              <ModeTitle style={{ color: '#4ade80' }}>ECO Mode</ModeTitle>
              <ModeSubtitle>Safe driving and fuel efficiency</ModeSubtitle>
              <ModeText>
                Eco mode reduces throttle response to the level you decide—giving you the potential to improve fuel
                efficiency up to 20%.
              </ModeText>
            </ModeCard>

            <ModeCard>
              <ModeTitle style={{ color: '#60a5fa' }}>CITY Mode</ModeTitle>
              <ModeSubtitle>Perfect for city driving</ModeSubtitle>
              <ModeText>
                Allows more active throttle response and shortens the acceleration time of the vehicle for smooth city
                navigation.
              </ModeText>
            </ModeCard>

            <ModeCard>
              <ModeTitle style={{ color: '#fbbf24' }}>SPORT Mode</ModeTitle>
              <ModeSubtitle>Makes driving interesting</ModeSubtitle>
              <ModeText>
                Provides a more engaged driving experience by increasing throttle response by up to 70%. Sport mode is
                the highest mode recommended for daily use.
              </ModeText>
            </ModeCard>

            <ModeCard>
              <ModeTitle style={{ color: '#ef4444' }}>SPORT+ Mode</ModeTitle>
              <ModeSubtitle>Gets extreme</ModeSubtitle>
              <ModeText>
                By removing all acceleration limits of your vehicle, you get a throttle response quick off the mark and
                in corners - recommended for track use.
              </ModeText>
            </ModeCard>
          </ModesGrid>

          <VehicleFilterSection>
            <FilterTitle>Find Your Pedal Power Kit</FilterTitle>
            <FilterDescription>Select your vehicle specifications to find a compatible Pedal Power Kit</FilterDescription>

            <FiltersGrid>
              <FilterDropdown>
                <label htmlFor="ppk-year">Year</label>
                <select id="ppk-year" value={vehicleFilters.year} onChange={(e) => handleVehicleFilterChange('year', e.target.value)}>
                  <option value="">Select Year</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </FilterDropdown>

              <FilterDropdown>
                <label htmlFor="ppk-make">Make</label>
                <select id="ppk-make" value={vehicleFilters.make_id} onChange={(e) => handleVehicleFilterChange('make_id', e.target.value)} disabled={!vehicleFilters.year}>
                  <option value="">Select Make</option>
                  {dropdownData.make?.map((make) => (
                    <option key={make.id} value={make.id}>{formatMakeName(make.title)}</option>
                  ))}
                </select>
              </FilterDropdown>

              <FilterDropdown>
                <label htmlFor="ppk-model">Model</label>
                <select id="ppk-model" value={vehicleFilters.model_name} onChange={(e) => handleVehicleFilterChange('model_name', e.target.value)} disabled={!vehicleFilters.make_id}>
                  <option value="">Select Model</option>
                  {deduplicateModels(dropdownData.name).map((model) => (
                    <option key={model.name} value={model.name}>{model.title}</option>
                  ))}
                </select>
              </FilterDropdown>

              <FilterDropdown>
                <label htmlFor="ppk-engine">Engine</label>
                <select id="ppk-engine" value={vehicleFilters.engine} onChange={(e) => handleVehicleFilterChange('engine', e.target.value)} disabled={!vehicleFilters.model_name}>
                  <option value="">Select Engine</option>
                  {dropdownData.engine?.map((eng) => (
                    <option key={eng.displacement} value={eng.displacement}>
                      {eng.displacement}
                    </option>
                  ))}
                </select>
              </FilterDropdown>
            </FiltersGrid>

            <FilterActions>
              <SearchButton onClick={handleSearch} disabled={!allFiltersSelected}>
                Find Matching Pedal Power Kits
              </SearchButton>
              {isFiltering && (
                <ClearButton onClick={handleClearFilters}>
                  Clear Filters
                </ClearButton>
              )}
            </FilterActions>

            {isFiltering && (
              <ProductCount $hasResults={tableData.length > 0}>
                {tableData.length > 0 ? (
                  <>Showing {tableData.length} throttle controller{tableData.length !== 1 ? 's' : ''} for {vehicleFilters.year} {getCurrentMakeName()} {vehicleFilters.model_name}</>
                ) : (
                  <>No throttle controllers found for {vehicleFilters.year} {getCurrentMakeName()} {vehicleFilters.model_name}. Try clearing filters to see all available throttle controllers.</>
                )}
              </ProductCount>
            )}
          </VehicleFilterSection>

          <TableIntro>
            {isFiltering
              ? 'Throttle controllers matching your vehicle are shown below. Click column headings to sort.'
              : 'Select a product from the list below. The table can be sorted by clicking on a column heading. All measurements are in inches, and the table may scroll left and right on smaller screens.'
            }
          </TableIntro>

          <FilterTable headers={tableHeaders} tableData={tableData} />

          <FAQSection faqs={pedalPowerKitFaqs} title="FAQs About Pedal Power Kit" />

          <ClosingSection>
            <ClosingText>
              Need help choosing the right performance bundle?{' '}
              <a href="/shop-by-vehicle">Shop by vehicle</a> or <a href="/contact-us">contact Green Filter</a> for
              expert assistance.
            </ClosingText>
          </ClosingSection>
        </PageWrapper>
      </main>

      <Footer />
    </>
  );
};

export default PowerPedalKitPage;

// Styled Components - Matching Harley Filter Page Exactly
const PageWrapper = styled.div`
  padding: 20px;
  background: #fff;

  h1 {
    font-family: Lato, sans-serif;
    font-size: 38px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 20px 20px;
  }

  .resize {
    max-width: 100%;
    height: auto;
  }

  @media (max-width: 767px) {
    padding: 15px;

    h1 {
      font-size: 26px;
      margin: 0 0 16px;
    }
  }
`;

const IntroText = styled.p`
  max-width: 800px;
  margin: 0 auto 30px;
  padding: 0 20px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 40px 20px 20px;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 20px;
    margin: 30px 15px 15px;
  }
`;

const GuideText = styled.p`
  max-width: 800px;
  margin: 0 auto 30px;
  padding: 0 20px;
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #555;
  line-height: 1.6;
  text-align: center;

  a {
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    font-size: 14px;
    margin-bottom: 20px;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  max-width: 1200px;
  margin: 0 auto 10px;
  padding: 0 20px;

  @media (max-width: 1024px) {
    gap: 30px;
  }
`;

const ImageBox = styled.div`
  text-align: center;

  img {
    max-width: 22%;
    height: auto;
  }

  @media (max-width: 1024px) {
    img {
      max-width: 40%;
    }
  }

  @media (max-width: 767px) {
    img {
      max-width: 60%;
    }
  }
`;

const BenefitsSection = styled.div`
  max-width: 900px;
  margin: 30px auto;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  clear: both;

  @media (max-width: 767px) {
    margin: 20px auto;
    padding: 15px;
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const BenefitItem = styled.li`
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: #333;
  padding-left: 20px;
  position: relative;
  line-height: 1.5;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${COLORS.brandGreen};
    font-weight: 700;
    font-size: 16px;
  }

  @media (max-width: 767px) {
    font-size: 13px;
  }
`;

const ModesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  max-width: 1200px;
  margin: 30px auto;
  padding: 0 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const ModeCard = styled.div`
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e5e5;

  @media (max-width: 767px) {
    padding: 15px;
  }
`;

const ModeTitle = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;

  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

const ModeSubtitle = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 10px 0;

  @media (max-width: 767px) {
    font-size: 13px;
  }
`;

const ModeText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: #555;
  margin: 0;
  line-height: 1.6;

  @media (max-width: 767px) {
    font-size: 13px;
  }
`;

const TableIntro = styled.p`
  max-width: 900px;
  margin: 40px auto 30px;
  padding: 0 20px;
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #555;
  line-height: 1.6;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 14px;
    margin: 30px auto 20px;
  }
`;

const ClosingSection = styled.div`
  max-width: 800px;
  margin: 30px auto 0;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  text-align: center;

  @media (max-width: 767px) {
    padding: 15px;
    margin-top: 20px;
  }
`;

const ClosingText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 15px;
  color: #555;
  margin: 0;
  line-height: 1.6;

  a {
    color: ${COLORS.brandGreen};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const VehicleFilterSection = styled.div`
  max-width: 960px;
  margin: 40px auto;
  background: linear-gradient(145deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 2px solid #444;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.08);

  @media (max-width: 767px) {
    padding: 24px 20px;
    margin: 30px 5%;
    width: 90%;
    max-width: none;
    box-sizing: border-box;
  }
`;

const FilterTitle = styled.h3`
  margin: 0 0 20px;
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #fff;

  @media (max-width: 767px) {
    font-size: 18px;
    text-align: center;
    margin-bottom: 16px;
  }
`;

const FilterDescription = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-style: italic;
  color: #fff;
  margin: 0 0 24px 0;
  text-align: center;
  line-height: 1.5;

  @media (max-width: 767px) {
    font-size: 13px;
    margin-bottom: 20px;
  }
`;

const FiltersGrid = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const FilterDropdown = styled.div`
  flex: 1;
  min-width: 140px;
  position: relative;

  &::after {
    content: '▼';
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    pointer-events: none;
    font-size: 10px;
  }

  label {
    display: none;
  }

  select {
    width: 100%;
    padding: 12px 36px 12px 14px;
    font-family: Lato, sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #aaa;
    background-color: #2a2a2a;
    border: 2px solid #555;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;

    &:hover {
      border-color: #777;
    }

    &:focus {
      outline: none;
      border-color: ${COLORS.brandGreen};
      color: #fff;
    }

    &:disabled {
      background-color: #1a1a1a;
      border-color: #444;
      color: #666;
      cursor: not-allowed;
      opacity: 0.6;
    }

    &:not([value=""]) {
      color: #fff;
    }

    option {
      background-color: #3a3a3a;
      color: #fff;
      padding: 14px 16px;
      font-family: Lato, sans-serif;
      font-size: 15px;
      font-weight: 400;
      line-height: 1.6;
    }

    option:first-child {
      color: #aaa;
    }
  }
`;

const FilterActions = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const SearchButton = styled.button`
  padding: 12px 32px;
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  background-color: ${COLORS.brandGreen};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 173, 35, 0.3);
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #00c728;
  }

  &:disabled {
    background-color: #2a2a2a;
    color: #777;
    border: 2px solid #444;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ClearButton = styled.button`
  padding: 12px 24px;
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #aaa;
  background-color: transparent;
  border: 2px solid #555;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.brandGreen};
    color: ${COLORS.brandGreen};
  }
`;

const ProductCount = styled.div<{ $hasResults?: boolean }>`
  text-align: center;
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$hasResults !== false ? COLORS.brandGreen : '#ff4444'};
  margin: 16px 0 0 0;
  padding: 12px 20px;
  background: ${props => props.$hasResults !== false ? 'rgba(70, 255, 61, 0.1)' : 'rgba(255, 68, 68, 0.1)'};
  border-radius: 6px;
  border: 1px solid ${props => props.$hasResults !== false ? 'rgba(70, 255, 61, 0.2)' : 'rgba(255, 68, 68, 0.2)'};

  @media (max-width: 767px) {
    font-size: 13px;
    padding: 10px 16px;
  }
`;
